import axios from "axios";

const BaseURL = process.env.REACT_APP_BaseURL;
const ANONYMOUS_LOGIN_PASSWORD = process.env.REACT_APP_ANONYMOUS_LOGIN_PASSWORD || "";
const TOKEN_BUFFER_MS = 15 * 1000;
const QUIZ_ANON_STORAGE_KEY = "zubrus_quiz_anonymous_session";

const DEFAULT_HEADERS = {
  "X-Timezone-Id": "Europe/Vilnius",
  "X-Version": "v3",
  "Content-Type": "application/json",
  "Cache-Control": "no-cache",
};

let cachedToken = null;
let cachedRefreshToken = null;
let tokenExpiresAt = 0;
let tokenPromise = null;

const quizApiClient = axios.create({
  baseURL: BaseURL,
  headers: DEFAULT_HEADERS,
});

function decodeJwtPayload(token) {
  try {
    const part = String(token).split(".")[1];
    if (!part) return null;
    const normalized = part.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(normalized));
  } catch {
    return null;
  }
}

function tokenRoles(accessToken) {
  return decodeJwtPayload(accessToken)?.realm_access?.roles || [];
}

function isQuizUserToken(accessToken) {
  const roles = tokenRoles(accessToken);
  return roles.includes("zubrus_student") || roles.includes("zubrus_user");
}

function accessTokenExpiresAt(accessToken, expiresIn) {
  const payload = decodeJwtPayload(accessToken);
  if (payload?.exp) return payload.exp * 1000;
  const sec = Number(expiresIn);
  if (Number.isFinite(sec) && sec > 0) return Date.now() + sec * 1000;
  return 0;
}

function readStoredQuizSession() {
  try {
    const raw = localStorage.getItem(QUIZ_ANON_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStoredQuizSession(session) {
  try {
    localStorage.setItem(QUIZ_ANON_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // storage may be unavailable
  }
}

function clearStoredQuizSession() {
  try {
    localStorage.removeItem(QUIZ_ANON_STORAGE_KEY);
  } catch {
    // storage may be unavailable
  }
}

function applyQuizTokens(tokens, userInfo) {
  if (!tokens?.accessToken) return null;
  cachedToken = tokens.accessToken;
  cachedRefreshToken = tokens.refreshToken || cachedRefreshToken;
  tokenExpiresAt = accessTokenExpiresAt(tokens.accessToken, tokens.expiresIn);
  const stored = readStoredQuizSession() || {};
  writeStoredQuizSession({
    tokens: { ...(stored.tokens || {}), ...tokens },
    userInfo: userInfo || stored.userInfo || null,
  });
  return cachedToken;
}

function clearMemoryTokens() {
  cachedToken = null;
  cachedRefreshToken = null;
  tokenExpiresAt = 0;
}

function isTokenValid() {
  return Boolean(
    cachedToken &&
      isQuizUserToken(cachedToken) &&
      tokenExpiresAt - TOKEN_BUFFER_MS > Date.now()
  );
}

function hydrateFromStorage() {
  const stored = readStoredQuizSession();
  const tokens = stored?.tokens;
  if (!tokens?.accessToken || !isQuizUserToken(tokens.accessToken)) return null;
  cachedToken = tokens.accessToken;
  cachedRefreshToken = tokens.refreshToken || null;
  tokenExpiresAt = accessTokenExpiresAt(tokens.accessToken, tokens.expiresIn);
  if (isTokenValid()) return cachedToken;
  return null;
}

async function loginAnonymousService() {
  const response = await axios.post(
    `${BaseURL}/auth/login/anonymous`,
    { password: ANONYMOUS_LOGIN_PASSWORD },
    { headers: DEFAULT_HEADERS }
  );
  const accessToken = response?.data?.data?.tokens?.accessToken;
  if (!accessToken) {
    throw new Error("Anonymous login failed: no access token received");
  }
  return accessToken;
}

async function registerAnonymousQuizUser() {
  const serviceToken = await loginAnonymousService();
  const response = await axios.post(
    `${BaseURL}/auth/register/anonymous`,
    {},
    { headers: { ...DEFAULT_HEADERS, Authorization: `Bearer ${serviceToken}` } }
  );
  const data = response?.data?.data;
  if (!data?.tokens?.accessToken || !isQuizUserToken(data.tokens.accessToken)) {
    throw new Error("Anonymous registration failed: no quiz user token received");
  }
  return applyQuizTokens(data.tokens, data.userInfo);
}

async function refreshQuizUserToken() {
  const refreshToken = cachedRefreshToken || readStoredQuizSession()?.tokens?.refreshToken;
  if (!refreshToken) return null;

  const endpoints = ["/auth/token/refresh", "/auth/refresh"];
  for (const path of endpoints) {
    try {
      const headers = { ...DEFAULT_HEADERS };
      if (cachedToken) headers.Authorization = `Bearer ${cachedToken}`;
      const response = await axios.post(
        `${BaseURL}${path}`,
        { refreshToken },
        { headers }
      );
      const payload = response?.data?.data || response?.data;
      const tokens = payload?.tokens || payload;
      if (tokens?.accessToken && isQuizUserToken(tokens.accessToken)) {
        return applyQuizTokens(tokens);
      }
    } catch {
      // try the next known refresh path
    }
  }
  return null;
}

async function ensureQuizUserToken({ forceNew = false, forceRefresh = false } = {}) {
  if (!forceNew && !forceRefresh && isTokenValid()) return cachedToken;
  if (!forceNew && !forceRefresh && !cachedToken) {
    const stored = hydrateFromStorage();
    if (stored) return stored;
  }

  if (!forceNew) {
    const refreshed = await refreshQuizUserToken();
    if (refreshed) return refreshed;
  }

  if (forceNew) {
    clearStoredQuizSession();
    clearMemoryTokens();
  }

  return registerAnonymousQuizUser();
}

export async function getToken() {
  if (isTokenValid()) return cachedToken;
  if (tokenPromise) return tokenPromise;
  tokenPromise = ensureQuizUserToken();
  try {
    return await tokenPromise;
  } finally {
    tokenPromise = null;
  }
}

quizApiClient.interceptors.request.use(async (config) => {
  const token = await getToken();
  config.headers["Authorization"] = `Bearer ${token}`;
  config.headers["X-Timezone-Id"] = DEFAULT_HEADERS["X-Timezone-Id"];
  config.headers["X-Version"] = DEFAULT_HEADERS["X-Version"];
  return config;
});

quizApiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error?.response?.status;
    const usedWrongAnonymousRole =
      status === 403 && cachedToken && !isQuizUserToken(cachedToken);

    if (
      originalRequest &&
      !originalRequest._retry &&
      (status === 401 || usedWrongAnonymousRole)
    ) {
      originalRequest._retry = true;
      tokenPromise = null;
      const newToken = await ensureQuizUserToken({
        forceNew: usedWrongAnonymousRole,
        forceRefresh: status === 401,
      });
      originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
      return quizApiClient(originalRequest);
    }
    return Promise.reject(error);
  }
);

export async function quizSearchDecks({ search = "", lastId = "", pageSize = 8, mainSubject = null } = {}) {
  const url = lastId
    ? `/contents/decks/search?lastId=${lastId}&pageSize=${pageSize}`
    : `/contents/decks/search?pageSize=${pageSize}`;

  const body = {
    deckName: search,
    countries: ["LT"],
    status: "PUBLISHED",
  };

  if (mainSubject) {
    body.mainSubject = mainSubject;
  }

  const response = await quizApiClient.post(url, body);
  return response?.data;
}

export async function quizGetDeckFilters({ countries = ["LT"] } = {}) {
  const response = await quizApiClient.post("/contents/decks/get-filters", {
    countries,
  });
  return response?.data;
}

export async function quizGetDeckDetail(deckId) {
  const response = await quizApiClient.get(`/contents/decks/${deckId}`);
  return response?.data;
}

export async function quizGetDeckCards(deckId) {
  const response = await quizApiClient.get(`/contents/decks/${deckId}/cards`);
  return response?.data;
}

/* -------------------------------------------------------------------------- */
/*                            Live Quiz (Kahoot-like)                          */
/* -------------------------------------------------------------------------- */

// Create a live quiz from selected cards (teacher).
// Returns: { uuidQuiz, shortId }
export async function quizCreate({ cardIds, secondsPerAnswer = 30 }) {
  const response = await quizApiClient.post(`/quiz`, { cardIds, secondsPerAnswer });
  return response?.data?.data;
}

// Join a quiz as a participant (student).
// Returns: { uuidParticipant }  (backend responds with the field "uuidParticipan")
export async function quizJoin({ quizShortId, name }) {
  const response = await quizApiClient.post(`/quiz/join`, { quizShortId, name });
  const data = response?.data?.data || {};
  return { uuidParticipant: data.uuidParticipant || data.uuidParticipan };
}

// Advance the quiz to the next question (teacher).
// Returns: { questionsLeft, status }  (status: "ACTIVE" | "FINISHED")
export async function quizNext({ uuidQuiz }) {
  const response = await quizApiClient.post(`/quiz/next`, { uuidQuiz });
  return response?.data?.data;
}

// Submit a participant's answer (student).
export async function quizAnswer({ quizShortId, uuidParticipant, cardId, answerText, timeTakenMs }) {
  const response = await quizApiClient.post(`/quiz/answer`, {
    quizShortId,
    uuidParticipant,
    cardId,
    answerText,
    timeTakenMs,
  });
  return response?.data;
}

// Fetch the quiz leaderboard / participant results.
// Returns: [{ name, uuidParticipant, joinedAt, answers: [{ cardId, status, timeTakenMs, timestamp }] }]
export async function quizLeaderboard({ uuidQuiz }) {
  const response = await quizApiClient.post(`/quiz/leaderboard`, { uuidQuiz });
  return response?.data?.data || [];
}

/* ----------------------------- SSE event stream ---------------------------- */
// The backend wraps every SSE field line with an extra "data:" prefix, so a raw
// message block looks like:
//   data:id: <ulid>
//   data:event: <EVENT_TYPE>
//   data:data: {<json with eventType & payload>}
// We strip the outer "data:" and JSON.parse the inner "data: {...}" line.
function parseSSEBlock(block) {
  const innerLines = block
    .split("\n")
    .map((line) => (line.startsWith("data:") ? line.slice(5) : line));

  for (const line of innerLines) {
    if (line.startsWith("data: ")) {
      const jsonStr = line.slice(6).trim();
      if (!jsonStr || jsonStr.startsWith(":")) return null; // heartbeat / empty
      try {
        return JSON.parse(jsonStr);
      } catch {
        return null;
      }
    }
  }
  return null;
}

// Generic fetch-based SSE client. Native EventSource cannot send the required
// Authorization header, so we stream the response body manually and auto-reconnect.
// `onEvent` receives the parsed event object ({ eventType, payload, ... }).
// Returns an unsubscribe function.
function createQuizEventSource(path, { onEvent, onStatus } = {}) {
  let stopped = false;
  let controller = null;

  async function connect() {
    while (!stopped) {
      try {
        const token = await getToken();
        controller = new AbortController();
        const res = await fetch(`${BaseURL}${path}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "text/event-stream",
            "X-Timezone-Id": DEFAULT_HEADERS["X-Timezone-Id"],
            "X-Version": DEFAULT_HEADERS["X-Version"],
          },
          signal: controller.signal,
        });

        if (res.status === 401) {
          cachedToken = null;
          tokenExpiresAt = 0;
          tokenPromise = null;
          throw new Error("SSE unauthorized");
        }
        if (!res.ok || !res.body) throw new Error(`SSE connect failed: ${res.status}`);

        onStatus?.("open");
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (!stopped) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          let sepIndex;
          while ((sepIndex = buffer.indexOf("\n\n")) >= 0) {
            const block = buffer.slice(0, sepIndex);
            buffer = buffer.slice(sepIndex + 2);
            const event = parseSSEBlock(block);
            if (event && event.eventType) onEvent?.(event);
          }
        }
      } catch (err) {
        if (stopped) break;
        onStatus?.("reconnecting");
      }
      if (!stopped) await new Promise((resolve) => setTimeout(resolve, 1500));
    }
  }

  connect();

  return () => {
    stopped = true;
    try {
      controller?.abort();
    } catch {
      /* noop */
    }
  };
}

// Teacher real-time stream: PARTICIPANT_JOINED_EVENT, PARTICIPANT_ANSWER_EVENT.
export function subscribeQuizMaster(uuidQuiz, handlers) {
  return createQuizEventSource(`/quiz/sse/master-subscribe/${uuidQuiz}`, handlers);
}

// Student real-time stream: NEXT_QUESTION_EVENT (card === null means quiz finished).
export function subscribeQuizClient(quizShortId, uuidParticipant, handlers) {
  return createQuizEventSource(
    `/quiz/sse/subscribe/${quizShortId}/${uuidParticipant}`,
    handlers
  );
}
